import axios from "axios";
import qs from "qs";
import {
  devModeOptions,
  getTranslations,
  getUrl,
  isRemoteApiEnabled,
  urlNormalizer,
  cookieBridge,
  getCorrelationHeaders,
} from "root/libs/core-libs/src";
import { COMMON } from "root/libs/constants/src";

import apisConfig from "../config/apis";
import { LAYOUT_FLAVOR } from "../containers/SpotShipmentPage/constants";

const { hosts } = apisConfig.urls;
const POSTAL_LOCATION_QUERY_CACHE_TTL_MS = 5 * 60 * 1000;
const POSTAL_LOCATION_QUERY_MAX_CACHE_ENTRIES = 200;
const postalLocationQueryCache = new Map();

const toStableString = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(toStableString).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `"${key}":${toStableString(value[key])}`)
      .join(",")}}`;
  }

  return JSON.stringify(value);
};

const getPostalLocationCacheKey = ({ params, canaryVersion, key, url }) =>
  `${url}|${key || "locationAutocomplete"}|${
    canaryVersion || ""
  }|${toStableString(params || {})}`;

const getApiBaseUrl = (adapter) => {
  switch (adapter) {
    case "quote":
      return hosts.quoteBaseUrl;
    default:
      return hosts.mainApiUrl;
  }
};

const setCanaryVersionHeader = (canaryVersion) =>
  canaryVersion && { [COMMON.CANARY_HEADER]: canaryVersion };

const setMicroFrontendIdHeader = (microFrontendId) =>
  microFrontendId && { [COMMON.MICRO_FRONTEND_ID_HEADER]: microFrontendId };

const getPathnameHeader = () => {
  if (window) {
    const { pathname } = window.location;
    return { [COMMON.PATHNAME_HEADER]: pathname };
  }
  return {};
};

const getHeaderForTargetServiceCard = () => {
  const cookieValue = cookieBridge.getCookie(
    COMMON.TARGET_SERVICE_CARD_COOKIE.name
  );
  if (cookieValue) {
    return {
      [COMMON.TARGET_SERVICE_CARD_COOKIE.name]: cookieValue,
    };
  }
  return {};
};

const buildQueryString = (params) =>
  qs
    .stringify(params, {
      allowDots: true,
      addQueryPrefix: true,
      encodeValuesOnly: true,
    })
    .replace(/(\[)/g, "(")
    .replace(/(\])/g, ")");

function get(
  endpoint,
  options = { params: "", adapter: "", canaryVersion: "", microFrontendId: "" }
) {
  const useRemote = isRemoteApiEnabled({ devMode: devModeOptions.API });
  const apiBaseUri = !useRemote ? hosts.root : getApiBaseUrl(options.adapter);
  const finalEndpoint = getUrl({
    endpoint,
    urls: apisConfig.urls,
    apiBaseUri,
  });
  const queryString = buildQueryString(options.params) || "";

  return axios.get(`${finalEndpoint}${queryString}`, {
    headers: {
      ...getCorrelationHeaders(),
      ...getPathnameHeader(),
      ...getHeaderForTargetServiceCard(),
      ...setCanaryVersionHeader(options.canaryVersion),
      ...setMicroFrontendIdHeader(options.microFrontendId),
    },
  });
}

function setupTranslations() {
  const { gaqTranslationsBaseUrl } = apisConfig.urls.hosts;

  return getTranslations(
    (locale) => {
      const [language] = locale.toLowerCase().split("-");
      if (language === "ar") {
        return import(
          "one-time-shipment/mocks/translations/_default_ar.json"
        ).then((module) => module.default);
      }
      return import("one-time-shipment/mocks/translations/_default.json").then(
        (module) => module.default
      );
    },
    (locale) => {
      const [language, country] = locale.toLowerCase().split("-");

      return `${gaqTranslationsBaseUrl}/${country}/${country}-${language}/content-api/one-time-shipment.data.json`;
    }
  );
}

// Webpack needs static import targets; avoid `import(`${expr}`)`.
const ICON_SVG_IMPORTS = {
  nlEnvelope: () => import("iconsTwoColors/cx/DHL_Preset-NL-Envelope.svg"),
  nlBooks: () => import("iconsTwoColors/cx/DHL_Preset-NL-Books.svg"),
  nlShoebox: () => import("iconsTwoColors/cx/DHL_Preset-NL-Shoebox.svg"),
  nlMovingbox: () => import("iconsTwoColors/cx/DHL_Preset-NL-MovingBox.svg"),
  usEnvelope: () =>
    import("iconsTwoColors/cx/DHL_Preset-US-DocumentEnvelope.svg"),
  usBooks: () => import("iconsTwoColors/cx/DHL_Preset-US-Books.svg"),
  usBoardgame: () => import("iconsTwoColors/cx/DHL_Preset-US-BoardGame.svg"),
  usMovingbox: () => import("iconsTwoColors/cx/DHL_Preset-US-MovingBox.svg"),
  usKitchenappliance: () =>
    import("iconsTwoColors/cx/DHL_Preset-US-KitchenAppliance.svg"),
  nlPaletEur1: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-EUR-1-120x80.svg"),
  nlPaletEur3: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-EUR-3-120x100.svg"),
  nlPaletEur6: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-EUR-6-80x60.svg"),
  auStandardPallet: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-AU-Standard-116x116.svg"),
  auExportPallet: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-AU-Export-110x110.svg"),
  us1Pallet: () => import("iconsTwoColors/cx/DHL_Preset-Pallet-US-1-48x40.svg"),
  mxStandardPallet: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-MX-Standard-120x120.svg"),
  eur1Pallet: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-EUR-1-120x80.svg"),
  eur3Pallet: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-EUR-3-120x100.svg"),
  eur6Pallet: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-EUR-6-80x60.svg"),
  cp3Pallet: () =>
    import("iconsTwoColors/cx/DHL_Preset-Pallet-CP3-114x114.svg"),
};
const FREIGHT_ICON_SVG_IMPORTS = {
  ...ICON_SVG_IMPORTS,
  nlEnvelope: () => import("iconsMultiColor/cx/preset-a4-envelope.svg"),
  nlShoebox: () => import("iconsMultiColor/cx/preset-medium-box.svg"),
  usEnvelope: () => import("iconsMultiColor/cx/preset-a4-envelope.svg"),
  nlBooks: () => import("iconsMultiColor/cx/preset-small-box.svg"),
  nlMovingbox: () => import("iconsMultiColor/cx/preset-xl-box.svg"),
  usBooks: () => import("iconsMultiColor/cx/preset-small-box.svg"),
  usBoardgame: () => import("iconsMultiColor/cx/preset-medium-box.svg"),
  usMovingbox: () => import("iconsMultiColor/cx/preset-xl-box.svg"),
};

const fetchSvgForPresets = (presets, layout) =>
  // 1. We make a list of promises that will be called for every icon.
  Promise.all(
    presets.map((item) => {
      const importSvg = (layout === LAYOUT_FLAVOR.FREIGHT
        ? FREIGHT_ICON_SVG_IMPORTS
        : ICON_SVG_IMPORTS)[item.icon];
      if (!importSvg) {
        return Promise.resolve({ default: undefined });
      }

      return importSvg();
    })
  ).then((res) =>
    // 3. We then get the response of that call and put the svg inside the `svg` property.
    presets.map((b, index) => ({
      ...b,
      svg: res[index].default,
    }))
  );

const getResponseWithSvgs = (data) => {
  // 1. We declare the promises that will be called to fetch the svgs for business preset sizes.
  const businessPromises = fetchSvgForPresets(
    data.spotShipmentPresetSizes.business,
    data.layoutFlavor
  );

  // 2. We declare the promises that will be called to fetch the svgs for private preset sizes.
  const privatePromises = fetchSvgForPresets(
    data.spotShipmentPresetSizes.private,
    data.layoutFlavor
  );

  // 3. We now call all the promises in parallel and adjust our object with the responses
  // we got from them.
  return Promise.all([businessPromises, privatePromises]).then(
    ([businessWithSvg, privateWithSvg]) => ({
      ...data,
      spotShipmentPresetSizes: {
        weightBreakThreshold: data.spotShipmentPresetSizes.weightBreakThreshold,
        business: businessWithSvg,
        private: privateWithSvg,
      },
    })
  );
};

function getCountrySettings(country, experience) {
  const useRemote = isRemoteApiEnabled({ devMode: devModeOptions.SETTINGS });

  const apiBaseUri = !useRemote ? hosts.root : getApiBaseUrl();

  if (useRemote) {
    const url = getUrl({
      endpoint: "countrySettings",
      urls: apisConfig.urls,
      apiBaseUri,
    });

    return axios
      .get(
        // eslint-disable-next-line prefer-template
        urlNormalizer(`${url}/${country}${experience ? "/" + experience : ""}`)
      )
      .then(({ data }) => getResponseWithSvgs(data));
  }

  return import(
    "one-time-shipment/mocks/settings/_default.json"
  ).then((module) => getResponseWithSvgs(module.default));
}

function getPostalLocationCountries(params, canaryVersion) {
  const apiBaseUri = getApiBaseUrl();

  const url = getUrl({
    endpoint: "postalCodePlaceholder",
    urls: apisConfig.urls,
    apiBaseUri,
  });

  return axios
    .get(
      // eslint-disable-next-line prefer-template
      urlNormalizer(url),
      {
        params: {
          ...params,
        },
        headers: {
          ...getCorrelationHeaders(),
          ...setCanaryVersionHeader(canaryVersion),
        },
      }
    )
    .then((response) => response.data);
}

function post({ endpoint, options = { payload: {}, adapter: "" } }) {
  const useRemote = isRemoteApiEnabled({ devMode: devModeOptions.API });
  const apiBaseUri = !useRemote ? hosts.root : getApiBaseUrl(options.adapter);

  const finalEndpoint = getUrl({
    endpoint,
    urls: apisConfig.urls,
    apiBaseUri,
  });
  const headers = {
    "Content-Type": "application/vnd.dhl.logistics.quotation+json;version=1",
    ...getCorrelationHeaders(),
  };

  return axios.post(`${finalEndpoint}`, options.payload, {
    headers,
  });
}

function getPostalLocationQuery(params, canaryVersion, key) {
  const { urls } = apisConfig;
  const url = key ? urls.endpoints[key] : urls.endpoints.locationAutocomplete;
  const cacheKey = getPostalLocationCacheKey({
    params,
    canaryVersion,
    key,
    url,
  });
  const now = Date.now();
  const cachedEntry = postalLocationQueryCache.get(cacheKey);

  if (cachedEntry && cachedEntry.expiresAt > now) {
    return cachedEntry.promise;
  }

  const request = axios({
    baseURL: urls.hosts.postalLocationBaseUrl,
    url,
    params: {
      ...params,
    },
    headers: {
      ...getCorrelationHeaders(),
      ...setCanaryVersionHeader(canaryVersion),
    },
  }).catch((error) => {
    postalLocationQueryCache.delete(cacheKey);
    throw error;
  });

  if (
    postalLocationQueryCache.size >= POSTAL_LOCATION_QUERY_MAX_CACHE_ENTRIES
  ) {
    const firstInsertedKey = postalLocationQueryCache.keys().next().value;
    if (firstInsertedKey) {
      postalLocationQueryCache.delete(firstInsertedKey);
    }
  }

  postalLocationQueryCache.set(cacheKey, {
    promise: request,
    expiresAt: now + POSTAL_LOCATION_QUERY_CACHE_TTL_MS,
  });

  return request;
}

function logPowerBIEvent(endpoint) {
  axios.get(endpoint).catch(function catchBlock(error) {
    if (typeof error.response === "undefined") {
      // eslint-disable-next-line no-console
      console.warn(`A network error occurred.`);
    } else {
      throw new Error(error.message);
    }
  });
}

export default {
  get,
  post,
  getPostalLocationQuery,
  setupTranslations,
  getCountrySettings,
  getPathnameHeader,
  getPostalLocationCountries,
  logPowerBIEvent,
};
